import React, { useState } from 'react'
import { message, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Merchant } from '../types/merchant.types'
import { merchantsService, type CreateMerchantRequest, type UpdateMerchantRequest } from '../../../shared/api/services/merchants.service'

export const useMerchantActions = () => {
    const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null)
    const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false)
    const [isFormModalOpen, setIsFormModalOpen] = useState(false)
    const queryClient = useQueryClient()

    // Mutations
    const createMutation = useMutation({
        mutationFn: (data: CreateMerchantRequest) => merchantsService.createMerchant(data),
        onSuccess: () => {
            message.success('Merchant created successfully')
            setIsFormModalOpen(false)
            queryClient.invalidateQueries({ queryKey: ['merchants'] })
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to create merchant')
        }
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateMerchantRequest }) => 
            merchantsService.updateMerchant(id, data),
        onSuccess: () => {
            message.success('Merchant updated successfully')
            setIsFormModalOpen(false)
            queryClient.invalidateQueries({ queryKey: ['merchants'] })
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to update merchant')
        }
    })

    const verifyKycMutation = useMutation({
        mutationFn: (id: string) => merchantsService.verifyKyc(id),
        onSuccess: () => {
            message.success('KYC verified successfully')
            if (selectedMerchant) setIsDetailDrawerOpen(false)
            queryClient.invalidateQueries({ queryKey: ['merchants'] })
        },
        onError: (error: any) => {
            const errorMsg = error.response?.data?.error?.message || error.response?.data?.message || 'Failed to verify KYC'
            message.error(errorMsg)
        }
    })



    const rejectMerchantMutation = useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) => merchantsService.rejectMerchant(id, reason),
        onSuccess: () => {
            message.success('Merchant rejected successfully')
            if (selectedMerchant) setIsDetailDrawerOpen(false)
            queryClient.invalidateQueries({ queryKey: ['merchants'] })
        },
        onError: (error: any) => {
             message.error(error.response?.data?.message || 'Failed to reject merchant')
        }
    })

    const unblockMerchantMutation = useMutation({
        mutationFn: (id: string) => merchantsService.unblockMerchant(id),
        onSuccess: () => {
            message.success('Merchant unblocked successfully')
            if (selectedMerchant) setIsDetailDrawerOpen(false)
            queryClient.invalidateQueries({ queryKey: ['merchants'] })
        },
        onError: (error: any) => {
             message.error(error.response?.data?.message || 'Failed to unblock merchant')
        }
    })

    const handleViewDetails = (merchant: Merchant) => {
        setSelectedMerchant(merchant)
        setIsDetailDrawerOpen(true)
    }

    const handleAddMerchant = () => {
        setSelectedMerchant(null)
        setIsFormModalOpen(true)
    }

    const handleVerifyKYC = (merchant: Merchant) => {
        Modal.confirm({
            title: 'Verify KYC',
            content: `Are you sure you want to approve KYC for ${merchant.business_name}?`,
            onOk: () => verifyKycMutation.mutate(merchant.merchant_id),
        })
    }

    const handleRejectMerchant = (merchant: Merchant) => {
        Modal.confirm({
            title: 'Reject Merchant',
            content: `Are you sure you want to reject ${merchant.business_name}?`,
            okText: 'Reject',
            okType: 'danger',
            onOk: () => rejectMerchantMutation.mutate({ id: merchant.merchant_id, reason: 'Admin rejection' }), 
        })
    }

    const handleUnblockMerchant = (merchant: Merchant) => {
        Modal.confirm({
            title: 'Unblock Merchant',
            content: `Are you sure you want to unblock ${merchant.business_name}?`,
            okText: 'Unblock',
            onOk: () => unblockMerchantMutation.mutate(merchant.merchant_id), 
        })
    }

    const handleBulkVerify = (merchantIds: React.Key[], onSuccess?: () => void) => {
        // Implement bulk actions in service if backend supports it, looping for now or creating a bulk endpoint
        // Assuming user wants single actions for now or we loop:
        // For simplicity/safety, let's just log or implement loop if needed.
        // But backend doesn't show bulk endpoints in my previous inspection.
        // I will implement a loop in the component or here if requested, but for now just placeholder or single calls.
        // Actually, let's just show a message that it's processing to avoid complexity unless requested.
         Modal.confirm({
            title: 'Verify Selected Merchants',
            content: `Are you sure you want to approve KYC for ${merchantIds.length} merchants?`,
            onOk: async () => {
                // Sequential for now as backend lacks bulk endpoint
                for (const id of merchantIds) {
                    await merchantsService.verifyKyc(String(id))
                }
                message.success(`${merchantIds.length} merchants verified successfully!`)
                queryClient.invalidateQueries({ queryKey: ['merchants'] })
                onSuccess?.()
            },
        })
    }

    const handleBulkReject = (merchantIds: React.Key[], onSuccess?: () => void) => {
        Modal.confirm({
            title: 'Reject Selected Merchants',
            content: `Are you sure you want to reject ${merchantIds.length} merchants?`,
            okText: 'Reject',
            okType: 'danger',
            onOk: async () => {
                 for (const id of merchantIds) {
                    await merchantsService.rejectMerchant(String(id), 'Bulk Rejection')
                }
                message.success(`${merchantIds.length} merchants rejected successfully!`)
                queryClient.invalidateQueries({ queryKey: ['merchants'] })
                onSuccess?.()
            },
        })
    }

    const handleFormSubmit = async (values: any) => {
        if (selectedMerchant) {
            await updateMutation.mutateAsync({ id: selectedMerchant.merchant_id, data: values })
        } else {
            // we will submit standard JSON.
            // Files will not be processed correctly by API Gateway automatically
            // They can be implemented later via presigned S3 URLs
            await createMutation.mutateAsync(values)
        }
    }

    const closeDrawer = () => setIsDetailDrawerOpen(false)
    const closeFormModal = () => setIsFormModalOpen(false)

    return {
        selectedMerchant,
        isDetailDrawerOpen,
        isFormModalOpen,
        handleViewDetails,
        handleAddMerchant,
        handleVerifyKYC,
        handleRejectMerchant,
        handleUnblockMerchant,
        handleBulkVerify,
        handleBulkReject,
        handleFormSubmit,
        closeDrawer,
        closeFormModal,
        isSubmitting: createMutation.isPending || updateMutation.isPending || unblockMerchantMutation.isPending || verifyKycMutation.isPending || rejectMerchantMutation.isPending
    }
}

